/**
 * Audio utility for workout timer sounds.
 * Uses Web Audio API for precise timing with HTML5 Audio fallback.
 */

type SoundType = 'warning' | 'setEnd' | 'restEnd' | 'complete';

export type SoundOption =
  | 'triple-beep'
  | 'double-beep'
  | 'single-beep'
  | 'rising-tone'
  | 'descending-tone'
  | 'low-beep'
  | 'high-beep'
  | 'bell';

// Audio context for Web Audio API
let audioContext: AudioContext | null = null;

// Preloaded audio buffers
const audioBuffers: Map<SoundType, AudioBuffer> = new Map();

// Fallback HTML5 Audio elements
const audioElements: Map<SoundType, HTMLAudioElement> = new Map();

// Sound file paths
const soundPaths: Record<SoundType, string> = {
  warning: '/audio/beep-warning.mp3',
  setEnd: '/audio/beep-set-end.mp3',
  restEnd: '/audio/beep-rest-end.mp3',
  complete: '/audio/beep-routine-end.mp3'
};

/**
 * Initialise the audio context (must be called after user interaction)
 */
export function initAudioContext(): void {
  if (!audioContext) {
    audioContext = new (
      window.AudioContext || (window as typeof window & { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    )();
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
}

/**
 * Preload all audio files for instant playback
 */
export async function preloadAudio(): Promise<void> {
  initAudioContext();

  const loadPromises = Object.entries(soundPaths).map(async ([type, path]) => {
    try {
      // Try Web Audio API first
      if (audioContext) {
        const response = await fetch(path);
        const arrayBuffer = await response.arrayBuffer();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
        audioBuffers.set(type as SoundType, audioBuffer);
      }
    } catch {
      // Fallback to HTML5 Audio
      const audio = new Audio(path);
      audio.preload = 'auto';
      audioElements.set(type as SoundType, audio);
    }
  });

  await Promise.all(loadPromises);
}

/**
 * Play a specific sound
 */
export function playSound(type: SoundType): void {
  // Try Web Audio API first
  const buffer = audioBuffers.get(type);
  if (audioContext && buffer) {
    const source = audioContext.createBufferSource();
    source.buffer = buffer;
    source.connect(audioContext.destination);
    source.start(0);
    return;
  }

  // Fallback to HTML5 Audio
  const audio = audioElements.get(type);
  if (audio) {
    audio.currentTime = 0;
    audio.play().catch(() => {
      // Silently fail if audio playback is blocked
      console.warn(`Could not play ${type} sound`);
    });
    return;
  }

  // Last resort: create new Audio element
  const fallbackAudio = new Audio(soundPaths[type]);
  fallbackAudio.play().catch(() => {
    console.warn(`Could not play ${type} sound`);
  });
}

/**
 * Generate a simple beep using Web Audio API oscillator
 * This is used as a fallback when audio files are not available
 */
export function playBeep(frequency: number = 440, duration: number = 200, type: OscillatorType = 'sine'): void {
  initAudioContext();

  if (!audioContext) return;

  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);

  gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration / 1000);

  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);

  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + duration / 1000);
}

/**
 * Play warning beeps (3 repeated high-pitch beeps)
 */
export function playWarningBeep(): void {
  playBeep(880, 100, 'sine');
  setTimeout(() => playBeep(880, 100, 'sine'), 200);
  setTimeout(() => playBeep(880, 100, 'sine'), 400);
}

/**
 * Play set end beep (medium pitch, medium duration)
 */
export function playSetEndBeep(): void {
  playBeep(660, 300, 'sine');
}

/**
 * Play rest end beep (rising tone effect)
 */
export function playRestEndBeep(): void {
  playBeep(523, 150, 'sine'); // C5
  setTimeout(() => playBeep(659, 150, 'sine'), 150); // E5
  setTimeout(() => playBeep(784, 200, 'sine'), 300); // G5
}

/**
 * Play completion sound (celebratory sequence)
 */
export function playCompletionBeep(): void {
  playBeep(523, 150, 'sine'); // C5
  setTimeout(() => playBeep(659, 150, 'sine'), 150); // E5
  setTimeout(() => playBeep(784, 150, 'sine'), 300); // G5
  setTimeout(() => playBeep(1047, 400, 'sine'), 450); // C6
}

/**
 * Play start beep (subtle confirmation)
 */
export function playStartBeep(): void {
  playBeep(440, 100, 'sine'); // A4 - short and subtle
}

/**
 * Play a specific sound option
 */
export function playSoundOption(option: SoundOption): void {
  switch (option) {
    case 'triple-beep':
      playBeep(880, 100, 'sine');
      setTimeout(() => playBeep(880, 100, 'sine'), 200);
      setTimeout(() => playBeep(880, 100, 'sine'), 400);
      break;
    case 'double-beep':
      playBeep(880, 150, 'sine');
      setTimeout(() => playBeep(880, 150, 'sine'), 300);
      break;
    case 'single-beep':
      playBeep(660, 300, 'sine');
      break;
    case 'rising-tone':
      playBeep(523, 150, 'sine'); // C5
      setTimeout(() => playBeep(659, 150, 'sine'), 150); // E5
      setTimeout(() => playBeep(784, 200, 'sine'), 300); // G5
      break;
    case 'descending-tone':
      playBeep(784, 150, 'sine'); // G5
      setTimeout(() => playBeep(659, 150, 'sine'), 150); // E5
      setTimeout(() => playBeep(523, 200, 'sine'), 300); // C5
      break;
    case 'low-beep':
      playBeep(330, 400, 'sine');
      break;
    case 'high-beep':
      playBeep(1047, 250, 'sine');
      break;
    case 'bell':
      playBeep(800, 100, 'triangle');
      setTimeout(() => playBeep(600, 200, 'triangle'), 150);
      break;
    default:
      playBeep(440, 200, 'sine');
  }
}
